import React from 'react';
import {
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  Play,
} from 'lucide-react';
import { SupportCall, CallDirection, CallStatus } from '../../types/support';
import {
  cn,
  formatDateTime,
  formatCallDuration,
  getCallStatusLabel,
} from '../../utils/helpers';
import Spinner from '../ui/Spinner';

interface SupportCallHistoryProps {
  calls: SupportCall[];
  loading: boolean;
}

const SupportCallHistory: React.FC<SupportCallHistoryProps> = ({
  calls,
  loading,
}) => {
  if (loading) {
    return (
      <div className="p-4 flex justify-center">
        <Spinner size="sm" />
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No call history
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {calls.map((call) => (
        <CallHistoryItem key={call.id} call={call} />
      ))}
    </div>
  );
};

interface CallHistoryItemProps {
  call: SupportCall;
}

const CallHistoryItem: React.FC<CallHistoryItemProps> = ({ call }) => {
  const isInbound = call.direction === CallDirection.INBOUND;
  const isCompleted = call.status === CallStatus.COMPLETED;
  const isMissed =
    call.status === CallStatus.NO_ANSWER ||
    call.status === CallStatus.BUSY ||
    call.status === CallStatus.FAILED ||
    call.status === CallStatus.CANCELED;

  const getStatusColor = () => {
    if (isCompleted) return 'text-success';
    if (isMissed) return 'text-destructive';
    return 'text-muted-foreground';
  };

  const Icon = isMissed
    ? PhoneMissed
    : isInbound
    ? PhoneIncoming
    : PhoneOutgoing;

  return (
    <div className="flex items-center gap-3 p-3 hover:bg-primary/5 transition-colors">
      <div
        className={cn(
          'p-2 rounded-full',
          isCompleted
            ? 'bg-success/20'
            : isMissed
            ? 'bg-destructive/20'
            : 'bg-secondary-700/50'
        )}
      >
        <Icon className={cn('h-4 w-4', getStatusColor())} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {isInbound ? 'Inbound' : 'Outbound'}
          </span>
          <span className={cn('text-xs', getStatusColor())}>
            {getCallStatusLabel(call.status)}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
          <span className="font-mono">{isInbound ? call.from : call.to}</span>
          <span>{formatDateTime(call.startedAt)}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {call.durationSeconds && call.durationSeconds > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {formatCallDuration(call.durationSeconds)}
          </div>
        )}
        {call.recordingUrl && (
          <button
            onClick={() => window.open(call.recordingUrl, '_blank')}
            className="p-1.5 rounded hover:bg-primary/10 text-primary transition-colors"
            title="Play recording"
          >
            <Play className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SupportCallHistory;
